/**
 * Texas Compliance API Service — complete layer
 * Wires every DoD-delivered Texas backend route group (21 groups) to the frontend.
 * All calls go through `landlordApi` (X-Jurisdiction header injected by api.ts).
 *
 * Covered route groups (all mounted at /api/texas/* in app.js):
 *   1. compliance-dashboard (F-146)   2. security-score (F-227/F-228)
 *   3. hardware (F-153)               4. notice (F-206/F-207)
 *   5. notice-vacate (F-133/F-206)    6. notice-consent (F-206)
 *   7. rekey (F-154)                  8. deposits (F-257–F-260)
 *   9. evidence (F-292)              10. lead-paint (F-155)
 *  11. dallas/registration (F-224/F-225/F-229)
 *  12. dallas/registration-score     13. dallas/inspection (F-140)
 *  14. dallas/audit-cycle (F-141)    15. houston/asec (F-143)
 *  16. houston/nondelegable (F-147)  17. houston/high-risk (F-144)
 *  18. sb38 (F-136)                  19. writ (F-135/F-230)
 *  20. hvac-readiness (F-139)        21. cares-act (F-230)
 *  22. appeal (F-136/F-133)
 */

import { landlordApi } from './api';
import {
  DepositItem,
  RekeyUnit,
  RentalRegistration,
  texasComplianceService,
} from './texasComplianceService';

// ── Generic response guard helpers ──────────────────────────────────────────
function dataArray(res: any, key = 'data'): any[] {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.[key])) return d[key];
  if (Array.isArray(d?.data)) return d.data;
  return [];
}

function dataObject(res: any): any {
  const d = res?.data;
  if (d && typeof d === 'object' && d.data !== null && typeof d.data === 'object') return d.data;
  return d || {};
}

// ── STR Portfolio Dashboard (F-249, E-044) ────────────────────────────────
export interface StrPortfolioOccupancy {
  currentGuests: number;
  maxOccupancy: number;
  utilizationPct: number;
  status: string;
  action: string;
  [key: string]: unknown;
}

export interface StrPortfolioProperty {
  propertyId: string;
  city: string;
  cityLabel?: string;
  registrationStatus: string;
  registrationActive: boolean;
  registrationSource?: string;
  strExpiryDate?: string | null;
  daysUntilExpiry?: number | null;
  alertTier?: string | null;
  annualFeeUsd?: number | null;
  portalUrl?: string | null;
  zone?: string | null;
  zoneDecision?: string | null;
  taxOwedUsd?: number | null;
  occupancy?: StrPortfolioOccupancy | null;
  [key: string]: unknown;
}

export interface StrPortfolioSummary {
  city: string;
  totalProperties: number;
  activeRegistrations: number;
  expiredRegistrations: number;
  unregisteredProperties: number;
  prohibitedZones: number;
  verificationRequired: number;
  occupancyWarnings: number;
  occupancyBlocks: number;
  totalAnnualTaxOwedUsd: number;
  registrationsByStatus: Record<string, number>;
  [key: string]: unknown;
}

export interface StrPortfolio {
  tenantId: string;
  city: string;
  asOf: string;
  properties: StrPortfolioProperty[];
  summary: StrPortfolioSummary;
  cities: string[];
  [key: string]: unknown;
}

// ── Compliance Dashboard (F-146, E-025) ────────────────────────────────────
export interface CompliancePortfolioSummary {
  propertyCount: number;
  registrationStatus: Record<string, number>;
  score: number;
  expiringCount: number;
  expiredCount: number;
  noticeCount?: number;
  topRisks?: Array<{ propertyId: string; propertyName: string; score: number; reasons: string[] }>;
  [key: string]: unknown;
}

// ── Security & Safety Score (F-227/F-228, E-046) ───────────────────────────
export interface SecurityPillarScore {
  id: string;
  name: string;
  score: number;
  maxScore: number;
  passing?: boolean;
  details?: Record<string, unknown>;
}

export interface SecurityScoreResult {
  overallScore: number;
  overallMax: number;
  passed: boolean;
  pillars: SecurityPillarScore[];
  propertyId?: string;
  evaluatedAt?: string;
  [key: string]: unknown;
}

// ── Subchapter D Hardware Checklist (F-153, E-027) ─────────────────────────
export interface HardwareChecklist {
  propertyId: string;
  devices: Record<string, unknown>[];
  certifierId?: string;
  certifiedAt?: string;
  [key: string]: unknown;
}

// ── Notices (F-206/F-207) ──────────────────────────────────────────────────
export interface NoticeToVacate {
  id: string;
  propertyId: string;
  tenantId?: string;
  noticeDate: string;
  expiryDate: string;
  daysRemaining: number;
  served: boolean;
  status: 'active' | 'expired' | 'cancelled' | 'served';
  [key: string]: unknown;
}

// ── Dallas Self-Inspection (F-140) ─────────────────────────────────────────
export interface DallasChecklist {
  propertyId: string;
  checklistId: string;
  status: 'pending' | 'passed' | 'failed' | 'submitted';
  [key: string]: unknown;
}

// ── Houston ASEC (F-143) ───────────────────────────────────────────────────
export interface HoustonCitation {
  propertyId: string;
  citationNumber?: string;
  issueDate: string;
  abatementDeadline?: string;
  status: 'open' | 'pending_remediation' | 'closed'
 | 'scheduled' | 'awaiting_signoff' | 'completed';
  [key: string]: unknown;
}

// ── Houston High-Risk / HRRB (F-144) ───────────────────────────────────────
export interface HrrbStatus {
  propertyId: string;
  registered: boolean;
  trainingCompleted: boolean;
  trainingDeadline?: string;
  [key: string]: unknown;
}

// ── SB 38 (F-136) ──────────────────────────────────────────────────────────
export interface Sb38Timeline {
  caseId: string;
  trialDate?: string;
  serviceDate?: string;
  writEligibleDate?: string;
  appealDeadline?: string;
  [key: string]: unknown;
}

// ── Writ of Possession (F-135/F-230) ───────────────────────────────────────
export interface WritTimeline {
  writId: string;
  issuedDate?: string;
  lockoutDate?: string;
  status: 'calculated' | 'issued' | 'lockout_scheduled' | 'closed';
  [key: string]: unknown;
}

// ── Lead-Based Paint (F-155) ───────────────────────────────────────────────
export interface LeadPaintStatus {
  propertyId: string;
  builtBefore1978: boolean;
  pamphletAcknowledged: boolean;
  disclosureSigned: boolean;
  blocker?: string | null;
  [key: string]: unknown;
}

// ── HVAC Freeze Readiness (F-139) ──────────────────────────────────────────
export interface HvacReadinessSummary {
  propertyCount: number;
  readyCount: number;
  atRiskCount: number;
  [key: string]: unknown;
}

export interface EvidenceItem {
  id: string;
  evidenceId: string;
  propertyId: string;
  propertyName?: string;
  evidenceType: string;
  fileName: string;
  fileSizeBytes?: number;
  sha256Checksum?: string;
  status: 'active' | 'pending' | 'archived';
  uploadedBy?: string;
  uploadedAt?: string;
  retentionYears?: number;
  metadata?: Record<string, unknown>;
}

export const texasApi = {
  // 1. Compliance Dashboard (F-146, E-025)
  complianceDashboard: {
    getMeta: () => landlordApi.get('api/texas/compliance-dashboard/meta'),
    getPortfolio: async (): Promise<CompliancePortfolioSummary> =>
      dataObject(await landlordApi.get('api/texas/compliance-dashboard/portfolio')),
    // POST /portfolio contract: { properties: [...] } of the documented
    // scoring fields. Raw /api/properties records use `id` (not propertyId),
    // carry mixed-case cities and embed certificate arrays that push the body
    // toward the express 100 kb JSON limit at portfolio scale — slim here.
    calculatePortfolio: async (properties: Record<string, unknown>[]) => {
      const slim = (properties || []).map((p: any) => ({
        propertyId: p.propertyId || p.id,
        city:
          (typeof p.city === 'string' && p.city.trim()) ||
          (p.address && typeof p.address === 'object' && p.address.city) ||
          '',
        registrationExpiry: p.registrationExpiry,
        lastInspectionDate: p.lastInspectionDate,
        nextInspectionDue: p.nextInspectionDue,
        citationCount: p.citationCount,
        hrrbStatus: p.hrrbStatus,
        violations: p.violations,
        remediationProgress: p.remediationProgress,
        address: typeof p.address === 'string' ? p.address : undefined,
      }));
      return dataObject(
        await landlordApi.post('api/texas/compliance-dashboard/portfolio', { properties: slim })
      );
    },
    getProperty: (propertyId: string) =>
      landlordApi.get(`api/texas/compliance-dashboard/property/${propertyId}`),
    getPropertyScore: async (propertyId: string): Promise<{ score: number; [k: string]: unknown }> =>
      dataObject(
        await landlordApi.get(`api/texas/compliance-dashboard/property/${propertyId}/score`)
      ),
  },

  // 2. Security & Safety Score (F-227/F-228, E-046)
  securityScore: {
    getMeta: () => landlordApi.get('api/texas/security-score/meta'),
    calculate: async (input: Record<string, unknown>): Promise<SecurityScoreResult> =>
      dataObject(await landlordApi.post('api/texas/security-score/calculate', input)),
    getProperty: async (propertyId: string): Promise<SecurityScoreResult> =>
      dataObject(await landlordApi.get(`api/texas/security-score/${propertyId}`)),
    exportProperty: (propertyId: string) =>
      landlordApi.get(`api/texas/security-score/${propertyId}/export`),
    getTenant: (tenantId: string) => landlordApi.get(`api/texas/security-score/tenant/${tenantId}`),
  },

  // 3. Subchapter D Hardware Checklist (F-153, E-027) — feature-gated
  hardware: {
    getMeta: () => landlordApi.get('api/texas/hardware/meta'),
    createChecklist: async (input: Record<string, unknown>): Promise<HardwareChecklist> =>
      dataObject(await landlordApi.post('api/texas/hardware/checklist', input)),
    getChecklist: async (propertyId: string): Promise<HardwareChecklist> =>
      dataObject(await landlordApi.get(`api/texas/hardware/checklist/${propertyId}`)),
    certify: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/hardware/certify', input),
    reinspect: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/hardware/reinspect', input),
    completeReinspection: (propertyId: string, deviceKey: string, input = {}) =>
      landlordApi.post(`api/texas/hardware/reinspect/${propertyId}/${deviceKey}/complete`, input),
    getCureStatus: (propertyId: string) =>
      landlordApi.get(`api/texas/hardware/cure-status/${propertyId}`),
    issueTermination: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/hardware/issue-termination', input),
  },

  // 4. Notices — Texas § 24 / lease notices (F-206/F-207)
  notices: {
    calculate: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/notice/calculate', input),
    evaluateCure: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/notice/cure', input),
    getHolidays: () => landlordApi.get('api/texas/notice/holidays'),
    validate: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/notice/validate', input),
    getMethods: () => landlordApi.get('api/texas/notice/methods'),
  },

  // 5. Notice to Vacate (F-133, F-206)
  noticeToVacate: {
    list: async (): Promise<NoticeToVacate[]> =>
      dataArray(await landlordApi.get('api/texas/notice-vacate')),
    create: async (input: Record<string, unknown>): Promise<NoticeToVacate> =>
      dataObject(await landlordApi.post('api/texas/notice-vacate', input)),
    cancel: (id: string) => landlordApi.post(`api/texas/notice-vacate/${id}/cancel`),
    remove: (id: string) => landlordApi.delete(`api/texas/notice-vacate/${id}`),
    getProperties: () => landlordApi.get('api/texas/notice-vacate/properties'),
    getTenants: (propertyId: string) =>
      landlordApi.get(`api/texas/notice-vacate/tenants/${propertyId}`),
  },

  // 6. Notice Consent (F-206)
  noticeConsent: {
    getMeta: () => landlordApi.get('api/texas/notice-consent/meta'),
    validate: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/notice-consent/validate', input),
    record: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/notice-consent/record', input),
    recordPaper: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/notice-consent/paper', input),
    getConsent: (id: string) => landlordApi.get(`api/texas/notice-consent/consent/${id}`),
  },

  // 7. 7-Day Re-keying Ledger (F-154, E-027) — feature-gated
  rekeyLedger: {
    getMeta: () => landlordApi.get('api/texas/rekey/meta'),
    getAll: async (): Promise<RekeyUnit[]> => {
      const res = await landlordApi.get('api/texas/rekey');
      return dataArray(res, 'rekeys');
    },
    activate: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/rekey/activate', input),
    getLedger: (propertyId: string) =>
      landlordApi.get(`api/texas/rekey/ledger/${propertyId}`),
    getTenantLedger: (propertyId: string, tenantId: string) =>
      landlordApi.get(`api/texas/rekey/ledger/${propertyId}/${tenantId}`),
    recordReceipt: (propertyId: string, tenantId: string, input = {}) =>
      landlordApi.post(`api/texas/rekey/ledger/${propertyId}/${tenantId}/receipt`, input),
    markCompliant: (propertyId: string, tenantId: string) =>
      landlordApi.post(`api/texas/rekey/ledger/${propertyId}/${tenantId}/compliant`),
    evaluate: (propertyId: string, tenantId: string) =>
      landlordApi.get(`api/texas/rekey/ledger/${propertyId}/${tenantId}/evaluate`),
    cure: (propertyId: string, tenantId: string, input = {}) =>
      landlordApi.post(`api/texas/rekey/ledger/${propertyId}/${tenantId}/cure`, input),
    terminate: (propertyId: string, tenantId: string) =>
      landlordApi.post(`api/texas/rekey/ledger/${propertyId}/${tenantId}/terminate`),
  },

  // 8. Security Deposit Tracker (F-257–F-260, E-047) — route-level Texas jurisdiction
  deposits: {
    getMeta: () => landlordApi.get('api/texas/deposits/meta'),
    getAll: async (): Promise<DepositItem[]> => {
      const res = await landlordApi.get('api/texas/deposits');
      return dataArray(res, 'deposits');
    },
    getSummary: async () =>
      dataObject(await landlordApi.get('api/texas/deposits/summary')),
    create: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/deposits', input),
    getCountdown: (tenancyId: string) =>
      landlordApi.get(`api/texas/deposits/${tenancyId}/countdown`),
    recordSurrender: (tenancyId: string, input = {}) =>
      landlordApi.post(`api/texas/deposits/${tenancyId}/surrender`, input),
    recordReturn: (tenancyId: string, input = {}) =>
      landlordApi.post(`api/texas/deposits/${tenancyId}/return`, input),
    getPenalty: (tenancyId: string) =>
      landlordApi.get(`api/texas/deposits/${tenancyId}/penalty`),
    getTenancy: (tenancyId: string) => landlordApi.get(`api/texas/deposits/${tenancyId}`),
  },

  // 9. Evidence Vault (F-292, US-235)
  evidence: {
    getMeta: () => landlordApi.get('api/evidence/types'),
    list: async (): Promise<EvidenceItem[]> => {
      const res = await landlordApi.get('api/evidence');
      return dataArray(res, 'items');
    },
    presignUpload: (input: Record<string, unknown>) =>
      landlordApi.post('api/evidence/presign-upload', input),
    confirmUpload: (input: Record<string, unknown>) =>
      landlordApi.post('api/evidence/confirm-upload', input),
    delete: (evidenceId: string) =>
      landlordApi.delete(`api/evidence/${evidenceId}`),
  },

  // 10. EPA Lead-Based Paint Disclosure (F-155, E-027)
  leadPaint: {
    getMeta: () => landlordApi.get('api/texas/lead-paint/meta'),
    list: async (): Promise<LeadPaintStatus[]> =>
      dataArray(await landlordApi.get('api/texas/lead-paint')),
    getProperty: async (propertyId: string): Promise<LeadPaintStatus> =>
      dataObject(await landlordApi.get(`api/texas/lead-paint/${propertyId}`)),
    createOrUpdate: (propertyId: string, input: Record<string, unknown>) =>
      landlordApi.post(`api/texas/lead-paint/${propertyId}`, input),
    recordPamphlet: (propertyId: string, input = {}) =>
      landlordApi.post(`api/texas/lead-paint/${propertyId}/pamphlet`, input),
    signDisclosure: (propertyId: string, input = {}) =>
      landlordApi.post(`api/texas/lead-paint/${propertyId}/sign`, input),
    getBlocker: (propertyId: string) =>
      landlordApi.get(`api/texas/lead-paint/${propertyId}/blocker`),
    getWarning: (propertyId: string) =>
      landlordApi.get(`api/texas/lead-paint/${propertyId}/warning`),
  },

  // 10. Dallas Rental Registration & Licensing (F-224/F-225/F-229)
  dallasRegistration: {
    register: async (input: Record<string, unknown>) =>
      dataObject(await landlordApi.post('api/texas/dallas/registration/register', input)),
    confirmPayment: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/dallas/registration/payment', input),
    submitAffidavit: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/dallas/registration/affidavit', input),
    getCertificate: (propertyId: string) =>
      landlordApi.get(`api/texas/dallas/registration/certificate/${propertyId}`),
    getPrepopulate: (propertyId: string) =>
      landlordApi.get(`api/texas/dallas/registration/prepopulate/${propertyId}`),
    getStatus: (propertyId: string) =>
      landlordApi.get(`api/texas/dallas/registration/status/${propertyId}`),
    getRenewals: () => landlordApi.get('api/texas/dallas/registration/renewals'),
    verifyEvictionEligibility: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/dallas/registration/verify-eviction-eligibility', input),
  },

  // 11. Dallas Registration Score (per-property score)
  dallasRegistrationScore: {
    getMeta: () => landlordApi.get('api/texas/dallas/registration-score/meta'),
    getProperty: (propertyId: string) =>
      landlordApi.get(`api/texas/dallas/registration-score/${propertyId}`),
    getStatus: (propertyId: string) =>
      landlordApi.get(`api/texas/dallas/registration-score/${propertyId}/status`),
    getPortfolioSummary: async () =>
      dataObject(
        await landlordApi.get('api/texas/dallas/registration-score/portfolio/summary')
      ),
  },

  // 12. Dallas Self-Inspection (F-140, E-025)
  dallasSelfInspection: {
    getMeta: () => landlordApi.get('api/texas/dallas/inspection/meta'),
    listChecklists: async (): Promise<DallasChecklist[]> =>
      dataArray(await landlordApi.get('api/texas/dallas/inspection/checklist')),
    createChecklist: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/dallas/inspection/checklist', input),
    getPropertyChecklists: async (propertyId: string): Promise<DallasChecklist[]> =>
      dataArray(await landlordApi.get(`api/texas/dallas/inspection/checklist/${propertyId}`)),
    getChecklist: (propertyId: string, checklistId: string) =>
      landlordApi.get(`api/texas/dallas/inspection/checklist/${propertyId}/${checklistId}`),
    getChecklistStatus: (propertyId: string, checklistId: string) =>
      landlordApi.get(`api/texas/dallas/inspection/checklist/${propertyId}/${checklistId}/status`),
    uploadPhotos: (propertyId: string, input = {}) =>
      landlordApi.post(`api/texas/dallas/inspection/checklist/${propertyId}/photos`, input),
    submitChecklist: (propertyId: string, input = {}) =>
      landlordApi.post(`api/texas/dallas/inspection/checklist/${propertyId}/submit`, input),
  },

  // 13. Dallas Audit Cycle (F-141, E-025)
  dallasAuditCycle: {
    getMeta: () => landlordApi.get('api/texas/dallas/audit-cycle/meta'),
    getDashboard: async () =>
      dataObject(await landlordApi.get('api/texas/dallas/audit-cycle/dashboard')),
    record: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/dallas/audit-cycle/record', input),
    getRecord: (propertyId: string) =>
      landlordApi.get(`api/texas/dallas/audit-cycle/record/${propertyId}`),
    scheduleInspection: (propertyId: string, input = {}) =>
      landlordApi.post(`api/texas/dallas/audit-cycle/record/${propertyId}/inspection`, input),
    updateSchedule: (propertyId: string, input = {}) =>
      landlordApi.post(`api/texas/dallas/audit-cycle/record/${propertyId}/schedule`, input),
    recordViolation: (propertyId: string, input = {}) =>
      landlordApi.post(`api/texas/dallas/audit-cycle/record/${propertyId}/violation`, input),
    updateViolation: (propertyId: string, violationId: string, input = {}) =>
      landlordApi.patch(`api/texas/dallas/audit-cycle/record/${propertyId}/violation/${violationId}`, input),
    raiseAlerts: (propertyId: string) =>
      landlordApi.post(`api/texas/dallas/audit-cycle/record/${propertyId}/alerts`),
  },

  // 14. Houston ASEC (F-143, E-025)
  houstonAsec: {
    getMeta: () => landlordApi.get('api/texas/houston/asec/meta'),
    recordCitations: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/houston/asec/citations', input),
    getCitations: async (propertyId: string): Promise<HoustonCitation[]> =>
      dataArray(
        await landlordApi.get(`api/texas/houston/asec/citations/${propertyId}`),
        'citations'
      ),
    recordInspection: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/houston/asec/inspection', input),
    completeInspection: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/houston/asec/inspection/complete', input),
    createRemediationPlan: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/houston/asec/remediation/plan', input),
    recordRemediationProgress: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/houston/asec/remediation/progress', input),
    signOff: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/houston/asec/signoff', input),
    close: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/houston/asec/close', input),
  },

  // 15. Houston Non-Delegable Duties (F-147, E-025)
  houstonNondelegable: {
    getMeta: () => landlordApi.get('api/texas/houston/nondelegable/meta'),
    recordObligations: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/houston/nondelegable/obligations', input),
    getObligations: (propertyId: string) =>
      landlordApi.get(`api/texas/houston/nondelegable/obligations/${propertyId}`),
    logCompliance: (obligationId: string, input = {}) =>
      landlordApi.post(`api/texas/houston/nondelegable/obligations/${obligationId}/log`, input),
    attachEvidence: (obligationId: string, input = {}) =>
      landlordApi.post(`api/texas/houston/nondelegable/obligations/${obligationId}/evidence`, input),
    closeObligation: (obligationId: string) =>
      landlordApi.post(`api/texas/houston/nondelegable/obligations/${obligationId}/close`),
  },

  // 16. Houston High-Risk / HRRB (F-144, E-025)
  houstonHighRisk: {
    getMeta: () => landlordApi.get('api/texas/houston/high-risk/meta'),
    getCitations: (propertyId: string) =>
      landlordApi.get(`api/texas/houston/high-risk/citations/${propertyId}`),
    recordCitations: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/houston/high-risk/citations', input),
    getHrrbStatus: async (propertyId: string): Promise<HrrbStatus> =>
      dataObject(await landlordApi.get(`api/texas/houston/high-risk/hrrb/status/${propertyId}`)),
    registerForHrrb: (propertyId: string) =>
      landlordApi.post(`api/texas/houston/high-risk/hrrb/register`, { propertyId }),
    completeHrrbTraining: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/houston/high-risk/hrrb/training', input),
    getPortfolioSummary: async () =>
      dataObject(await landlordApi.get('api/texas/houston/high-risk/portfolio/summary')),
    getTrigger: (propertyId: string) =>
      landlordApi.get(`api/texas/houston/high-risk/trigger/${propertyId}`),
  },

  // 17. SB 38 Accelerated Eviction Timelines (F-136, E-024)
  sb38: {
    getTimelines: async (input: Record<string, unknown>): Promise<Sb38Timeline[]> =>
      dataArray(await landlordApi.post('api/texas/sb38/timelines', input), 'timelines'),
    getCase: (caseId: string) =>
      landlordApi.get(`api/texas/sb38/timelines/${caseId}`),
    calculateTrial: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/sb38/timelines/trial', input),
    calculateService: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/sb38/timelines/service', input),
    calculateWrit: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/sb38/timelines/writ', input),
    summarize: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/sb38/timelines/summary', input),
    recordSwornDeclaration: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/sb38/timelines/sworn', input),
  },

  // 18. Writ of Possession (F-135/F-230, feature-gated)
  writ: {
    getMeta: () => landlordApi.get('api/texas/writ/meta'),
    calculate: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/writ/calculate', input),
    start: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/writ/start', input),
    get: async (writId: string): Promise<WritTimeline> =>
      dataObject(await landlordApi.get(`api/texas/writ/${writId}`)),
    recordLockoutChecklist: (writId: string, input = {}) =>
      landlordApi.post(`api/texas/writ/${writId}/lockout-checklist`, input),
    recordPersonalProperty: (writId: string, input = {}) =>
      landlordApi.post(`api/texas/writ/${writId}/personal-property`, input),
    close: (writId: string) => landlordApi.post(`api/texas/writ/${writId}/close`),
  },

  // 19. HVAC Freeze Readiness (F-139, E-025)
  hvacReadiness: {
    getMeta: () => landlordApi.get('api/texas/hvac-readiness/meta'),
    getPortfolioSummary: async (): Promise<HvacReadinessSummary> =>
      dataObject(await landlordApi.get('api/texas/hvac-readiness/portfolio/summary')),
    getProperty: (propertyId: string) =>
      landlordApi.get(`api/texas/hvac-readiness/${propertyId}`),
    checkReminders: (input = {}) =>
      landlordApi.post('api/texas/hvac-readiness/reminders/check', input),
    recordSummerTest: (propertyId: string, input = {}) =>
      landlordApi.post(`api/texas/hvac-readiness/${propertyId}/summer-test`, input),
    recordWinterChecklist: (propertyId: string, input = {}) =>
      landlordApi.post(`api/texas/hvac-readiness/${propertyId}/winter-checklist`, input),
    recordTemperatureLog: (propertyId: string, input = {}) =>
      landlordApi.post(`api/texas/hvac-readiness/${propertyId}/temperature-log`, input),
    getTemperatureLogs: (propertyId: string) =>
      landlordApi.get(`api/texas/hvac-readiness/${propertyId}/temperature-logs`),
    triggerWeatherAlert: (propertyId: string, input = {}) =>
      landlordApi.post(`api/texas/hvac-readiness/${propertyId}/weather-alert`, input),
    getWeatherAlerts: (propertyId: string) =>
      landlordApi.get(`api/texas/hvac-readiness/${propertyId}/weather-alerts`),
  },

  // 20. CARES Act Eviction Moratorium (F-230)
  caresAct: {
    getMeta: () => landlordApi.get('api/texas/cares-act/meta'),
    evaluate: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/cares-act/evaluate', input),
    override: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/cares-act/override', input),
    acknowledgeOverride: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/cares-act/override/acknowledge', input),
    setMortgageType: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/cares-act/mortgage-type', input),
    getAudit: (propertyId: string) =>
      landlordApi.get(`api/texas/cares-act/audit/${propertyId}`),
    getBanner: (propertyId: string) =>
      landlordApi.get(`api/texas/cares-act/banner/${propertyId}`),
  },

  // 21. Sworn Appeal (F-136/F-133, SB 38 appeal provisions)
  swornAppeal: {
    getMeta: () => landlordApi.get('api/texas/appeal/meta'),
    startAppeal: (caseId: string, input = {}) =>
      landlordApi.post(`api/texas/appeal/appeal/${caseId}`, input),
    getAppeal: (caseId: string) =>
      landlordApi.get(`api/texas/appeal/appeal/${caseId}`),
    recordCourtDeposit: (caseId: string, input = {}) =>
      landlordApi.post(`api/texas/appeal/appeal/${caseId}/deposit`, input),
    recordAppealFiled: (caseId: string, input = {}) =>
      landlordApi.post(`api/texas/appeal/appeal/${caseId}/appeal-filed`, input),
    closeAppeal: (caseId: string) =>
      landlordApi.post(`api/texas/appeal/appeal/${caseId}/close`),
  },

  // Re-expose the F-268 composite views (single source of truth)
  compliance: texasComplianceService,

  // 23. STR Portfolio Dashboard (F-249, E-044) — T-203-002 UI data layer
  strPortfolio: {
    getPortfolio: async (city?: string): Promise<StrPortfolio> =>
      dataObject(await landlordApi.get(
        city ? `api/texas/str-portfolio?city=${encodeURIComponent(city)}` : 'api/texas/str-portfolio'
      )),
    exportCsv: (city?: string): Promise<Blob> =>
      landlordApi.get(
        city ? `api/texas/str-portfolio/export?city=${encodeURIComponent(city)}` : 'api/texas/str-portfolio/export',
        { responseType: 'blob' }
      ).then((res: any) => res.data as Blob),
  },

  // 24. Integration Registry (F-271, E-048) — F-311 FE Console
  integrations: {
    getMeta: () => landlordApi.get('api/texas/integrations/meta'),
    getAdapters: () => landlordApi.get('api/texas/integrations'),
    getAdapterMeta: (adapterId: string) => landlordApi.get(`api/texas/integrations/${adapterId}/meta`),
    testInvoke: (adapterId: string, payload: Record<string, unknown> = {}) =>
      landlordApi.post(`api/texas/integrations/${adapterId}/lookup`, payload),
    verifyAdapter: (adapterId: string, payload: Record<string, unknown> = {}) =>
      landlordApi.post(`api/texas/integrations/${adapterId}/verify`, payload),
    refreshAdapter: (adapterId: string) =>
      landlordApi.post(`api/texas/integrations/${adapterId}/refresh`),
  },

  // 25. CCPA Compliance Checker (F-078, E-023) — F-311 FE Console
  ccpa: {
    getMeta: () => landlordApi.get('api/texas/ccpa/meta'),
    getSnapshot: () => landlordApi.get('api/texas/ccpa/snapshot'),
    runCheck: (input: Record<string, unknown> = {}) =>
      landlordApi.post('api/texas/ccpa/check', input),
    getViolations: () => landlordApi.get('api/texas/ccpa/violations'),
    checkResidency: () => landlordApi.get('api/texas/ccpa/residency'),
    checkStripe: (input: Record<string, unknown> = {}) =>
      landlordApi.post('api/texas/ccpa/stripe-check', input),
  },

  // 26. CCPA DSR Tools (F-080, E-023) — F-311 FE Console
  ccpaDsr: {
    getMeta: () => landlordApi.get('api/texas/ccpa/dsr/meta'),
    createRequest: (input: Record<string, unknown>) =>
      landlordApi.post('api/texas/ccpa/dsr/requests', input),
    listRequests: (status?: string) =>
      landlordApi.get(status ? `api/texas/ccpa/dsr/requests?status=${encodeURIComponent(status)}` : 'api/texas/ccpa/dsr/requests'),
    getRequest: (requestId: string) =>
      landlordApi.get(`api/texas/ccpa/dsr/requests/${requestId}`),
    verifyRequest: (requestId: string, input: Record<string, unknown> = {}) =>
      landlordApi.post(`api/texas/ccpa/dsr/requests/${requestId}/verify`, input),
    extendRequest: (requestId: string, input: Record<string, unknown> = {}) =>
      landlordApi.post(`api/texas/ccpa/dsr/requests/${requestId}/extend`, input),
    fulfillRequest: (requestId: string, input: Record<string, unknown> = {}) =>
      landlordApi.post(`api/texas/ccpa/dsr/requests/${requestId}/fulfill`, input),
    getAuditTrail: (requestId: string) =>
      landlordApi.get(`api/texas/ccpa/dsr/requests/${requestId}/audit`),
    runDeadlineSweep: (targetDate?: string) =>
      landlordApi.get(targetDate ? `api/texas/ccpa/dsr/deadlines/sweep?date=${encodeURIComponent(targetDate)}` : 'api/texas/ccpa/dsr/deadlines/sweep'),
  },
// 27. Event Stream Operations (F-254 / F-255 / F-256) - F-312 FE screen 1.
  // Mounted at /api/texas/kafka/* (app.js:581, 587, 593). Success envelope is
  // { success, data }; failure carries `message`.
  eventStream: {
    // F-254 topic catalog. The route reads req.query.owner and
    // req.query.schemaType (camelCase), NOT schema_type.
    listTopics: (filters: { owner?: string; schemaType?: string } = {}) => {
      const qs = new URLSearchParams();
      if (filters.owner) qs.append('owner', filters.owner);
      if (filters.schemaType) qs.append('schemaType', filters.schemaType);
      const suffix = qs.toString();
      return landlordApi.get(`api/texas/kafka/topic-catalog${suffix ? `?${suffix}` : ''}`);
    },
    getTopic: (topicName: string) =>
      landlordApi.get(`api/texas/kafka/topic-catalog/${encodeURIComponent(topicName)}`),
    // F-255 schema registry. GET compat returns the configured mode only
    // (BACKWARD | FORWARD | FULL). It is NOT a compatibility verdict and the FE
    // must never derive one - see F-312 AC6.
    listSchemas: (topicName: string) =>
      landlordApi.get(`api/texas/kafka/schema-registry/schemas/${encodeURIComponent(topicName)}`),
    getLatestSchema: (topicName: string) =>
      landlordApi.get(`api/texas/kafka/schema-registry/schemas/${encodeURIComponent(topicName)}/latest`),
    getCompatMode: (topicName: string) =>
      landlordApi.get(`api/texas/kafka/schema-registry/schemas/${encodeURIComponent(topicName)}/compat`),
    // F-256 consumer. GET /dlq returns { schemaDlq, processingDlq }.
    listRules: () => landlordApi.get('api/texas/kafka/alert-consumer/rules'),
    getRule: (eventType: string) =>
      landlordApi.get(`api/texas/kafka/alert-consumer/rules/${encodeURIComponent(eventType)}`),
    listDlq: (topicName?: string) =>
      landlordApi.get(topicName
        ? `api/texas/kafka/alert-consumer/dlq?topicName=${encodeURIComponent(topicName)}`
        : 'api/texas/kafka/alert-consumer/dlq'),
    getLag: () => landlordApi.get('api/texas/kafka/alert-consumer/lag'),
  },

  // 28. Template Library (F-269 registry + F-270 template set + F-267 form
  // schemas) - F-312 FE screen 2.
  // The template registry mounts at /api/templates (app.js:676), NOT
  // /api/texas/templates, and its list route is /templates inside it, so the
  // full read path is /api/templates/templates. Form schemas ARE under
  // /api/texas/form-schemas (app.js:599).
  // This namespace reports errors as `error`, not `message`, because
  // routes/templates.js builds { success:false, error } itself.
  templateLibrary: {
    listTemplates: () => landlordApi.get('api/templates/templates'),
    listVersions: (slug: string, filters: { jurisdiction?: string; locale?: string } = {}) => {
      const qs = new URLSearchParams();
      if (filters.jurisdiction) qs.append('jurisdiction', filters.jurisdiction);
      if (filters.locale) qs.append('locale', filters.locale);
      const suffix = qs.toString();
      return landlordApi.get(
        `api/templates/templates/${encodeURIComponent(slug)}/versions${suffix ? `?${suffix}` : ''}`
      );
    },
    // The real resolve-by-(slug, version, jurisdiction, locale) surface.
    resolve: (input: { slug: string; version?: string; jurisdiction?: string; locale?: string }) => {
      const qs = new URLSearchParams();
      qs.append('slug', input.slug);
      if (input.version) qs.append('version', input.version);
      if (input.jurisdiction) qs.append('jurisdiction', input.jurisdiction);
      if (input.locale) qs.append('locale', input.locale);
      return landlordApi.get(`api/templates/resolve?${qs.toString()}`);
    },
    // pdf-lib render. Returns { base64, pageCount, warnings, issues, template }.
    // 400 on field validation failure, 404 on unresolvable slug, 500 on
    // renderer error - all three are first-class FE states (AC7).
    render: (input: {
      slug: string;
      version?: string;
      jurisdiction?: string;
      locale?: string;
      data?: Record<string, unknown>;
    }) => landlordApi.post('api/templates/render', input),
    // F-267 supporting panel, folded into this screen per the F-312 ruling.
    listFormSchemas: (jurisdiction?: string) =>
      landlordApi.get(jurisdiction
        ? `api/texas/form-schemas?jurisdiction=${encodeURIComponent(jurisdiction)}`
        : 'api/texas/form-schemas'),
    listFormSchemaVersions: (entityType: string) =>
      landlordApi.get(`api/texas/form-schemas/${encodeURIComponent(entityType)}/versions`),
  },

  // 29. Deadline Monitor (F-135 TTL deadlines) - F-312 FE screen 3.
  // Mounted at /api/texas/ttl-deadlines (app.js:523). There is NO bare '/' on
  // that router - the read surfaces are /active, /summary and /policy.
  // /active?includeExpired=true is super-admin gated server-side (401/403).
  deadlineMonitor: {
    listActive: (filters: { family?: string; propertyId?: string; includeExpired?: boolean } = {}) => {
      const qs = new URLSearchParams();
      if (filters.family) qs.append('family', filters.family);
      if (filters.propertyId) qs.append('propertyId', filters.propertyId);
      if (filters.includeExpired) qs.append('includeExpired', 'true');
      const suffix = qs.toString();
      return landlordApi.get(`api/texas/ttl-deadlines/active${suffix ? `?${suffix}` : ''}`);
    },
    getSummary: () => landlordApi.get('api/texas/ttl-deadlines/summary'),
    getPolicy: () => landlordApi.get('api/texas/ttl-deadlines/policy'),
  },
};

export default texasApi;